import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, effect, inject, Injector, input, output, runInInjectionContext, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { clientSetDataFromGus, QuickListDataSourceFactory } from "@skylab-angular-shared/business";
import { EnumTranslator } from "@skylab-angular-shared/core";
import { TranslatePipe } from "@skylab-angular-shared/pipes";
import { ButtonComponent, DynamicListDirective, IconComponent, QuickListInsComponent, SelectValueAccessorDirective, TextValueAccessorDirective, TooltipDirective, ValidateDirective, ValidateInnerDirective, ValidationSourceDirective } from "@skylab-angular-shared/ui";
import { CacheableQuickListDataSourceBase, ClientBO, ClientQLCollectionItem, ClientQLCollectionQuery, CountryItem, EmailUtils, NipValidator, QuickListDataSourceFactoryBase, quickListFilterMethod, QuickListFilterMethod } from "@skylab-shared/business";
import { buildValidationKey, IdentifiableValueConverter } from "@skylab-shared/core";
import { ClientKind, ClientOtherBuyerRole, EnumTranslatorBase, LabeledEnumValueConverter, TinKind } from "@skylab-shared/dataModel";
import { BehaviorSubject } from "rxjs";
import { ViewValidation, ViewValidationService } from "../../../../form/form-validation";
import { ClientQuickListItemComponent } from "../../../../quick-list/quick-list-client-item/quick-list-client-item.component";

@Component({
    selector: 'skl-client-dialog-form',
    template: `
        @if (ready()) {
            <div class="grid" [sklValidationSource]="viewValidation!">
                <div class="col-1-3">
                    <ins-form-field
                        [label]="'scenes.shared.dialog.client.form.fields.client-kind.label' | translate">
                        <ins-select
                            [id]="getElementId('client-dialog-kind')"
                            display-text="label"
                            [readonly]="previousDataMode() || previewMode() || adjustmentMode()"
                            [converter]="clientKindValueConverter"
                            [(ngModel)]="bo().model.kind"
                            [sklValidate]="getValidationKey('kind')">
                            @for (item of clientKinds; track item) {
                                <ins-option
                                    [value]="item">
                                    {{item.label}}
                                </ins-option>
                            }
                        </ins-select>
                    </ins-form-field>
                </div>
                <div class="col-2-3">
                    <div class="input-button-wrapper">
                        <ins-form-field
                            [label]="(isCompany() ? 'scenes.shared.dialog.client.form.fields.name.label-company' : 'scenes.shared.dialog.client.form.fields.name.label-person') | translate">
                            <ins-input
                                [id]="getElementId('client-dialog-name')"
                                [skl-focus-by]
                                [readonly]="previousDataMode() || previewMode()!"
                                [(ngModel)]="bo().model.name"
                                [placeholder]="(isCompany() ? 'scenes.shared.dialog.client.form.fields.name.placeholder-company' : 'scenes.shared.dialog.client.form.fields.name.placeholder-person') | translate"
                                [sklValidate]="getValidationKey('name')">
                            </ins-input>
                        </ins-form-field>
                        @let msg = goToMessage();
                        @if (msg) {
                            <skl-button
                                (click)="redirectToClients.emit()"
                                [tooltip]="msg"
                                typeName="primary"
                                [sklValidateInner]="{key: getValidationKey('name'), option: 'hide'}"
                                [sklValidateInnerReadonly]="previousDataMode() || previewMode()!">
                                {{ 'scenes.shared.dialog.client.buttons.repair' | translate }}
                            </skl-button>
                        }
                    </div>
                </div>
                <div class="col-1">
                    <div class="col-1-3">
                        <ins-form-field
                            class="text-truncate"
                            [label]="'scenes.shared.dialog.client.form.fields.tin-kind.label' | translate">
                            <ins-select
                                [id]="getElementId('client-form-tin-kind')"
                                display-text="label"
                                [placeholder]="'ui.placeholders.empty' | translate"
                                [readonly]="previousDataMode() || previewMode() || adjustmentMode()"
                                [converter]="tinKindValueConverter"
                                [(ngModel)]="bo().model.tinKind"
                                [sklValidate]="getValidationKey('tinKind')">
                                @for (item of tinKinds; track item) {
                                    <ins-option
                                        [value]="item">
                                        {{item.label}}
                                    </ins-option>
                                }
                            </ins-select>
                        </ins-form-field>
                    </div>
                    <div class="col-2-3">
                        <div class="input-button-wrapper">
                            @let tinKindLabel = tinKindValueConverter.convert(bo().model.tinKind)?.label || '';
                            <ins-form-field
                                [label]="tinKindLabel">
                                <ins-input
                                    [id]="getElementId('client-dialog-tin')"
                                    (insInput)="setGusSearchButtonDisabled($event)"
                                    [(ngModel)]="bo().model.tin"
                                    [readonly]="previousDataMode() || previewMode() || adjustmentMode()"
                                    [placeholder]="'scenes.clients.form.fields.tax-identification-number.placeholder' | translate: { value: tinKindLabel }"
                                    [sklValidate]="getValidationKey('tin')">
                                </ins-input>
                            </ins-form-field>
                            @if (gusButtonVisible()) {
                                <skl-button
                                    [id]="getElementId('client-dialog-gus-searching-button')"
                                    typeName="primary"
                                    (click)="searchInGusAsync()"
                                    [disabled]="gusSearchButtonDisabled()"
                                    [spinner]="gusSearching()">
                                    {{ ('scenes.shared.dialog.client.form.buttons.search-in-gus.search') | translate }}
                                </skl-button>
                            }
                        </div>
                    </div>
                </div>
                <div class="col-1">
                    <ins-form-field
                        [label]="'scenes.shared.dialog.client.form.fields.address.label' | translate">
                        <ins-input
                            [id]="getElementId('client-dialog-address')"
                            [(ngModel)]="bo().model.address"
                            [readonly]="previousDataMode() || previewMode()!"
                            [placeholder]="'scenes.shared.dialog.client.form.fields.address.placeholder' | translate"
                            [sklValidate]="getValidationKey('address')">
                        </ins-input>
                    </ins-form-field>
                </div>
                <div class="col-1">
                    <div class="col-1-3">
                        <ins-form-field
                            [label]="'scenes.shared.dialog.client.form.fields.zip-code.label' | translate">
                            <ins-input
                                [id]="getElementId('client-dialog-zip-code')"
                                [(ngModel)]="bo().model.zipCode"
                                [readonly]="previousDataMode() || previewMode()!"
                                [placeholder]="'scenes.shared.dialog.client.form.fields.zip-code.placeholder' | translate"
                                [sklValidate]="getValidationKey('zipCode')">
                            </ins-input>
                        </ins-form-field>
                    </div>
                    <div class="col-1-3">
                        <ins-form-field
                            [label]="'scenes.shared.dialog.client.form.fields.city.label' | translate">
                            <ins-input
                                [id]="getElementId('client-dialog-city')"
                                [(ngModel)]="bo().model.city"
                                [readonly]="previousDataMode() || previewMode()!"
                                [placeholder]="'scenes.shared.dialog.client.form.fields.city.placeholder' | translate"
                                [sklValidate]="getValidationKey('city')">
                            </ins-input>
                        </ins-form-field>
                    </div>
                    <div class="col-1-3">
                        <ins-form-field
                            [label]="'scenes.shared.dialog.client.form.fields.country.label' | translate">
                            <ins-select
                                [id]="getElementId('client-dialog-country')"
                                display-text="name"
                                [converter]="countryValueConverter"
                                [readonly]="previousDataMode() || previewMode()!"
                                [(ngModel)]="bo().model.countryId"
                                [sklValidate]="getValidationKey('countryId')">
                                @for (item of countries(); track item) {
                                    <ins-option
                                        [value]="item">
                                        {{item.name}}
                                    </ins-option>
                                }
                            </ins-select>
                        </ins-form-field>
                    </div>
                </div>
                <div class="col-1">
                    <ins-form-field
                        [label]="'scenes.shared.dialog.client.form.fields.email.label' | translate">
                        <ins-input
                            [id]="getElementId('client-dialog-emails')"
                            [(ngModel)]="clientEmails"
                            [readonly]="previousDataMode() || previewMode()!"
                            [placeholder]="'scenes.shared.dialog.client.form.fields.email.placeholder' | translate"
                            [sklValidate]="[getValidationKey('emails'), getValidationKey('clientEmailsPlainText')]">
                        </ins-input>
                        <span slot="end">
                            <skl-icon
                                [id]="getElementId('client-dialog-emails-info')"
                                [tooltip]="'scenes.shared.dialog.client.form.fields.email.help-in-place' | translate"
                                color="secondary"
                                shape="circle"
                                name="help">
                            </skl-icon>
                        </span>
                    </ins-form-field>
                </div>
                @if (buyerVisible()) {
                    <div class="col-1-2 role-col">
                        <ins-form-field
                            class="text-truncate"
                            [label]="'scenes.shared.dialog.client.form.fields.buyer-role.label' | translate">
                            <ins-select
                                [id]="getElementId('client-dialog-buyer-role')"
                                display-text="label"
                                [converter]="buyerRolesConverter"
                                [placeholder]="'ui.placeholders.empty' | translate"
                                [readonly]="previousDataMode() || previewMode()!"
                                [(ngModel)]="bo().model.buyerRole"
                                [sklValidate]="getValidationKey('buyerRole')">
                                <ins-option
                                    [value]="null">
                                    {{'ui.placeholders.empty' | translate}}
                                </ins-option>
                                @for (item of buyerRoles; track item) {
                                    <ins-option
                                        [value]="item">
                                        {{ item.label }}
                                    </ins-option>
                                }
                                <skl-icon
                                    class="align--tiny-left"
                                    [tooltip]="('scenes.clients.form.fields.buyer-role.readonly-info' | translate)"
                                    color="secondary"
                                    shape="circle"
                                    name="help">
                                </skl-icon>
                            </ins-select>
                        </ins-form-field>
                    </div>
                    <div class="col-1-2 role-col">
                        <ins-form-field
                            class="text-truncate"
                            [label]="'scenes.clients.form.fields.internal-identification-number.label' | translate">
                            <ins-input
                                [id]="getElementId('client-dialog-internal-identification-number')"
                                [(ngModel)]="bo().model.internalIdentificationNumber"
                                [readonly]="previousDataMode() || previewMode() || adjustmentMode()"
                                [placeholder]="'scenes.clients.form.fields.internal-identification-number.placeholder' | translate"
                                [sklValidate]="getValidationKey('internalIdentificationNumber')">
                            </ins-input>
                            <div slot="end">
                                <skl-icon
                                    [tooltip]="('scenes.clients.form.fields.internal-identification-number.readonly-info' | translate) + '\n\n' + ('scenes.clients.form.fields.internal-identification-number.info' | translate)"
                                    color="secondary"
                                    shape="circle"
                                    name="help">
                                </skl-icon>
                            </div>
                        </ins-form-field>
                    </div>
                    <div class="col-1">
                        <ins-form-field
                            [label]="(supplierContext() ? 'scenes.shared.dialog.client.form.fields.supplier.label' : 'scenes.shared.dialog.client.form.fields.receiver.label') | translate">
                            <skl-quick-list-ins
                                [id]="getElementId('client-dialog-buyer')"
                                [locked]="true"
                                [(ngModel)]="buyer()!.value"
                                [disabled]="previousDataMode() || previewMode() || adjustmentMode()"
                                [displayText]="'name'"
                                [placeholder]="(supplierContext() ? 'scenes.shared.dialog.client.form.fields.supplier.placeholder' : 'scenes.shared.dialog.client.form.fields.receiver.placeholder') | translate"
                                [validationKey]="getValidationKey('buyerId')">
                                <ins-option
                                    class="option option--multiline"
                                    *sklDynamicList="let item of clientFilter"
                                    [value]="item">
                                    <skl-client-quick-list-item
                                        [data]="item"
                                        (onSwitch)="onClientFavouriteSwitch()">
                                    </skl-client-quick-list-item>
                                </ins-option>
                            </skl-quick-list-ins>
                        </ins-form-field>
                    </div>
                }
            </div>
        }
    `,
    styles: `
        .role-col {
            width: 50%;
        }

        @media only screen and (max-width: 85rem) {
            .role-col {
                width: 100%;
            }
        }
    `,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [ValidateInnerDirective, TooltipDirective, ValidationSourceDirective, TextValueAccessorDirective, FormsModule, ValidateDirective, SelectValueAccessorDirective, TranslatePipe, ButtonComponent, DynamicListDirective, ClientQuickListItemComponent, QuickListInsComponent, IconComponent]
})
export class ClientDialogFormComponent {
    bo = input.required<ClientBO>();

    previousDataMode = input(false);
    previewMode = input.required<boolean | null | undefined>();
    inEditMode = input(false);
    adjustmentMode = input.required<boolean>();

    supplierContext = input();
    buyerVisible = input();
    buyer = input<{ value: Partial<ClientQLCollectionItem> | null } | null | undefined>();
    showValidations = input<BehaviorSubject<boolean>>();
    countries = input.required<CountryItem[]>();

    goToMessage = input<string | undefined>('');
    redirectToClients = output();

    readonly #viewValidationService = inject(ViewValidationService);
    readonly #enumTranslator = inject(EnumTranslator) as EnumTranslatorBase;
    readonly #quickListDataSourceFactory = inject(QuickListDataSourceFactory) as QuickListDataSourceFactoryBase;
    readonly #destroyRef = inject(DestroyRef);
    readonly #injector = inject(Injector);

    clientKindValueConverter = new LabeledEnumValueConverter(this.#enumTranslator, ClientKind);
    clientKinds = this.#enumTranslator.getLabeledEnumValueArray(ClientKind);

    tinKindValueConverter= new LabeledEnumValueConverter(this.#enumTranslator, TinKind);
    tinKinds = this.#enumTranslator.getLabeledEnumValueArray(TinKind);

    buyerRolesConverter = new LabeledEnumValueConverter(this.#enumTranslator, ClientOtherBuyerRole);
    buyerRoles = this.#enumTranslator.getLabeledEnumValueArray(ClientOtherBuyerRole);

    countryValueConverter!: IdentifiableValueConverter;
    viewValidation!: ViewValidation | null;
    #clientDataSource: CacheableQuickListDataSourceBase<ClientQLCollectionQuery, ClientQLCollectionItem>;
    clientFilter!: QuickListFilterMethod<ClientQLCollectionItem>;

    ready = signal(false);
    gusSearchButtonDisabled = signal(true);
    gusSearching = signal(false);

    constructor() {
        this.#clientDataSource = this.#quickListDataSourceFactory.createCacheable<ClientQLCollectionQuery, ClientQLCollectionItem>(ClientQLCollectionQuery);

        this.#destroyRef.onDestroy(() => {
            this.viewValidation?.dispose();
            this.#clientDataSource?.dispose();
        });

        effect(() => {
            this.countryValueConverter = new IdentifiableValueConverter(this.countries());
            this.clientFilter = quickListFilterMethod<ClientQLCollectionQuery, ClientQLCollectionItem>(this.#clientDataSource);
            this.viewValidation = this.bo() && !this.previousDataMode() ? this.#viewValidationService.create([this.bo()], this.showValidations()) : null;
            this.gusSearchButtonDisabled.set(!NipValidator.isValid(this.bo().model.tin));
            this.ready.set(true);
        });
    }

    getValidationKey(propertyName: string): string {
        return buildValidationKey(this.bo().model.sun, propertyName);
    }

    getElementId(id: string): string {
        return this.previousDataMode() ? `previous-${id}` : id;
    }

    isCompany(): boolean {
        return this.bo().model.kind === ClientKind.Company;
    }

    isVAT(): boolean {
        return this.bo().model.tinKind === TinKind.NIP;
    }

    gusButtonVisible(): boolean {
        return this.isCompany() && this.isVAT() && !this.previewMode() && !this.adjustmentMode();
    }

    async searchInGusAsync(): Promise<void> {
        runInInjectionContext(this.#injector, async () => {
            this.gusSearching.set(true);
            await clientSetDataFromGus(this.bo());
            this.gusSearching.set(false);
        })
    }

    onClientFavouriteSwitch(): void {
        this.#clientDataSource?.clearCache();
    }

    async setGusSearchButtonDisabled(event: Event): Promise<void> {
        const val = (event.target as HTMLInsInputElement).value as string;
        this.gusSearchButtonDisabled.set(!NipValidator.isValid(val));
    };

    set clientEmails(val) {
        this.bo().model.clientEmailsPlainText = val;
        this.bo().model.emails = EmailUtils.extractEmails(val);
    }

    get clientEmails(): string {
        return EmailUtils.concatEmails(this.bo().model.emails) || '';
    }
}